"use client";

import { forwardRef, ComponentRef, ComponentPropsWithoutRef } from "react";
import { LucideIcon } from "lucide-react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@drpcrd/utils/shared";

/**
 * Tabs component for navigation and content switching.
 *
 * This component provides a complete tabs interface with customizable styling and layout options.
 * Perfect for creating navigation tabs, content switchers, or any interface that requires
 * multiple views with tab-based selection.
 *
 * Usage Examples:
 *
 * Basic content tabs:
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">Overview</TabsTrigger>
 *     <TabsTrigger value="tab2">Details</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">Overview content</TabsContent>
 *   <TabsContent value="tab2">Details content</TabsContent>
 * </Tabs>
 * ```
 *
 * Navigation tabs with routing:
 * ```tsx
 * // First, determine active tab from current route
 * const pathname = usePathname();
 * const router = useRouter();
 *
 * const getActiveTab = () => {
 *   if (pathname.includes('/saved')) return 'saved';
 *   if (pathname.includes('/browse')) return 'browse';
 *   return 'browse'; // default
 * };
 *
 * const handleTabChange = (value: string) => {
 *   switch (value) {
 *     case 'saved':
 *       router.push('/dashboard/saved');
 *       break;
 *     case 'browse':
 *       router.push('/dashboard/browse');
 *       break;
 *   }
 * };
 *
 * <Tabs value={getActiveTab()} onValueChange={handleTabChange}>
 *   <TabsList variant="hug">
 *     <TabsTrigger value="browse">Browse</TabsTrigger>
 *     <TabsTrigger value="saved" icon={Heart}>Saved</TabsTrigger>
 *   </TabsList>
 * </Tabs>
 * ```
 *
 * Full-width layout:
 * ```tsx
 * <TabsList variant="grow">
 *   <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *   <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 * </TabsList>
 * ```
 */

type TabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: "grow" | "hug";
};

export const Tabs = TabsPrimitive.Root;

export const TabsList = forwardRef<
  ComponentRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = "hug", ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "gap-1",
      variant === "grow" ? "flex w-full [&>button]:flex-1" : "inline-flex",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

type TabsTriggerProps = {
  icon?: LucideIcon;
  iconPosition?: "start" | "end";
  iconClassName?: string;
};

export const TabsTrigger = forwardRef<
  ComponentRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & TabsTriggerProps
>(
  (
    {
      className,
      icon: Icon,
      iconPosition = "start",
      iconClassName,
      children,
      ...props
    },
    ref,
  ) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "data-[state=active]:ring-border-tab-active data-[state=active]:text-text-headings data-[state=active]:bg-bg-tab-active data-[state=inactive]:ring-border-tab-disabled data-[state=inactive]:text-text-disabled data-[state=inactive]:bg-bg-tab-disabled data-[state=active]:ring-border-tab-active data-[state=inactive]:ring-border-tab-disabled flex cursor-pointer items-center justify-center gap-1 rounded-full px-2 py-1 align-middle text-sm leading-2.75 font-semibold ring-1 transition-colors ring-inset focus-visible:outline-hidden disabled:pointer-events-none disabled:bg-neutral-500 disabled:text-neutral-900 disabled:ring-neutral-500 data-[state=inactive]:hover:bg-white data-[state=inactive]:hover:text-black data-[state=inactive]:hover:ring-black data-[state=inactive]:hover:[&>svg]:text-black",
        iconPosition === "end" && "flex-row-reverse",
        className,
      )}
      {...props}
    >
      {Icon && <Icon className={iconClassName} />}
      {children}
    </TabsPrimitive.Trigger>
  ),
);

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const TabsContent = forwardRef<
  ComponentRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
